/**
 * Item Hierarchy Page
 * 
 * Displays all items in a hierarchical tree view based on:
 * - Parent/child relationships
 * - Altitude levels
 * 
 * Allows users to:
 * - Visualize the full item hierarchy
 * - Navigate to individual items
 * - Toggle between tree and grouped views
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Layers, 
  GitBranch, 
  LayoutGrid, 
  ArrowLeft,
  Filter,
  Search,
  Info,
} from 'lucide-react';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { HierarchyView, type HierarchyItem } from '../../components/prioritization/HierarchyView';
import { ALTITUDE_LEVELS } from '../../lib/altitudeValidation';
import { useAltitudeText } from '../../hooks/prioritization/useAltitudeText';
import type { ItemAltitude } from '../../types/prioritization/itemLifecycle';

type ViewMode = 'tree' | 'altitude';

export default function ItemHierarchy() {
  const altitudeText = useAltitudeText();
  const navigate = useNavigate();
  const items = useItemsStore(state => state.items);
  
  const [viewMode, setViewMode] = useState<ViewMode>('tree');
  const [selectedItemId, setSelectedItemId] = useState<string | undefined>();
  const [searchQuery, setSearchQuery] = useState('');
  const [altitudeFilter, setAltitudeFilter] = useState<ItemAltitude | 'all'>('all');
  
  // Convert items to hierarchy items
  const hierarchyItems: HierarchyItem[] = useMemo(() => {
    return items
      .filter(c => c.detailedState !== 'ARCHIVED')
      .map(c => ({
        id: c.id,
        title: c.title,
        altitude: c.altitude,
        parentItemId: c.parentItemId,
        childItemIds: c.childItemIds,
        description: c.description,
        status: c.detailedState,
      }));
  }, [items]);
  
  // Filter items based on search and altitude
  const filteredItems = useMemo(() => {
    let items = hierarchyItems;
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      items = items.filter(
        i => i.title.toLowerCase().includes(query) ||
             i.description?.toLowerCase().includes(query)
      );
    }
    
    if (altitudeFilter !== 'all') {
      items = items.filter(i => i.altitude === altitudeFilter);
    }
    
    return items;
  }, [hierarchyItems, searchQuery, altitudeFilter]);
  
  // Statistics
  const stats = useMemo(() => {
    const byAltitude: Record<string, number> = {};
    let withParent = 0;
    let withChildren = 0;
    
    for (const item of hierarchyItems) {
      const key = item.altitude || 'unset';
      byAltitude[key] = (byAltitude[key] || 0) + 1;
      
      if (item.parentItemId) withParent++;
      if (item.childItemIds && item.childItemIds.length > 0) withChildren++;
    }
    
    return { byAltitude, withParent, withChildren, total: hierarchyItems.length };
  }, [hierarchyItems]);
  
  const handleItemClick = (itemId: string) => {
    setSelectedItemId(itemId);
    // Could navigate to item detail: navigate(`/prioritization/items/${itemId}`);
  };
  
  const selectedItem = selectedItemId
    ? hierarchyItems.find(i => i.id === selectedItemId)
    : null;
  
  return (
    <div className="min-h-screen bg-[var(--surface)]">
      {/* Header */}
      <div className="bg-[var(--bg-raised)] border-b border-[var(--rule)]">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/prioritization')}
                className="p-2 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-[var(--text-pri)] flex items-center gap-2">
                  <GitBranch className="h-5 w-5 text-primary-600" />
                  Item Hierarchy
                </h1>
                <p className="text-sm text-[var(--text-ter)]">
                  Visualize parent-child relationships and altitude levels
                </p>
              </div>
            </div>
            
            {/* View Toggle */}
            <div className="flex items-center gap-2 bg-[var(--hover-tint)] p-1">
              <button
                onClick={() => setViewMode('tree')}
                className={`flex items-center gap-2 px-3 py-1.5  text-sm font-medium transition-colors ${
                  viewMode === 'tree'
                    ? 'bg-[var(--bg-raised)] text-[var(--text-pri)] shadow-sm'
                    : 'text-[var(--text-sec)] hover:text-[var(--text-pri)]'
                }`}
              >
                <GitBranch className="h-4 w-4" />
                Tree View
              </button>
              <button
                onClick={() => setViewMode('altitude')}
                className={`flex items-center gap-2 px-3 py-1.5  text-sm font-medium transition-colors ${
                  viewMode === 'altitude'
                    ? 'bg-[var(--bg-raised)] text-[var(--text-pri)] shadow-sm'
                    : 'text-[var(--text-sec)] hover:text-[var(--text-pri)]'
                }`}
              >
                <Layers className="h-4 w-4" />
                By Altitude
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-4">
            {/* Filters */}
            <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Search */}
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)]" />
                  <input
                    type="text"
                    placeholder="Search items..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-[var(--rule)] text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                
                {/* Altitude Filter */}
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-[var(--text-ter)]" />
                  <select
                    value={altitudeFilter}
                    onChange={(e) => setAltitudeFilter(e.target.value as ItemAltitude | 'all')}
                    className="bg-[var(--bg-raised)] text-[var(--text-pri)] border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="all">All Altitudes</option>
                    {ALTITUDE_LEVELS.map((alt) => (
                      <option key={alt} value={alt}>
                        {altitudeText.format(alt, null, { short: true })}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            
            {/* Hierarchy View */}
            <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
              <HierarchyView
                items={filteredItems}
                onItemClick={handleItemClick}
                selectedItemId={selectedItemId}
                groupByAltitude={viewMode === 'altitude'}
                showOrphans={true}
                maxDepth={10}
              />
            </div>
          </div>
          
          {/* Sidebar */}
          <div className="space-y-4">
            {/* Statistics */}
            <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
              <h3 className="font-medium text-[var(--text-pri)] mb-3 flex items-center gap-2">
                <LayoutGrid className="h-4 w-4 text-[var(--text-ter)]" />
                Statistics
              </h3>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-[var(--text-ter)]">Total Items</dt>
                  <dd className="font-medium text-[var(--text-pri)]">{stats.total}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-[var(--text-ter)]">With Parent</dt>
                  <dd className="font-medium text-[var(--text-pri)]">{stats.withParent}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-[var(--text-ter)]">With Children</dt>
                  <dd className="font-medium text-[var(--text-pri)]">{stats.withChildren}</dd>
                </div>
              </dl>
              
              <hr className="my-3 border-[var(--rule)]" />
              
              <h4 className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide mb-2">
                By Altitude
              </h4>
              <dl className="space-y-1 text-sm">
                {ALTITUDE_LEVELS.map((alt) => {
                  const count = stats.byAltitude[alt] || 0;
                  return (
                    <div key={alt} className="flex justify-between">
                      <dt className="text-[var(--text-ter)]">{altitudeText.format(alt, null, { short: true })}</dt>
                      <dd className="font-medium text-[var(--text-pri)]">{count}</dd>
                    </div>
                  );
                })}
                {stats.byAltitude['unset'] && (
                  <div className="flex justify-between">
                    <dt className="text-[var(--text-ter)]">Unset</dt>
                    <dd className="font-medium text-[var(--text-ter)]">{stats.byAltitude['unset']}</dd>
                  </div>
                )}
              </dl>
            </div>
            
            {/* Selected Item Detail */}
            {selectedItem ? (
              <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
                <h3 className="font-medium text-[var(--text-pri)] mb-3 flex items-center gap-2">
                  <Info className="h-4 w-4 text-[var(--text-ter)]" />
                  Selected Item
                </h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-[var(--text-pri)]">
                      {selectedItem.title}
                    </p>
                    {selectedItem.description && (
                      <p className="text-sm text-[var(--text-ter)] mt-1 line-clamp-3">
                        {selectedItem.description}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {selectedItem.altitude && (
                      <span className="text-xs px-2 py-1 bg-primary-50 text-primary-700 rounded-full">
                        {altitudeText.format(selectedItem.altitude)}
                      </span>
                    )}
                    {selectedItem.status && (
                      <span className="text-xs px-2 py-1 bg-[var(--hover-tint)] text-[var(--text-sec)] rounded-full">
                        {selectedItem.status}
                      </span>
                    )}
                  </div>
                  
                  <button
                    onClick={() => navigate(`/prioritization/items/${selectedItem.id}`)}
                    className="w-full px-3 py-2 text-sm text-primary-600 border border-primary-200 hover:bg-primary-50 transition-colors"
                  >
                    View Details
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-[var(--hover-tint)] border border-[var(--rule)] p-4 text-center text-[var(--text-ter)] text-sm">
                <Info className="h-5 w-5 mx-auto mb-2 text-[var(--text-ter)]" />
                Click an item to see details
              </div>
            )}
            
            {/* Legend */}
            <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
              <h3 className="font-medium text-[var(--text-pri)] mb-3">Altitude Levels</h3>
              <div className="space-y-2 text-sm">
                {ALTITUDE_LEVELS.map((alt) => {
                  const Icon = {
                    outcome: () => <span className="text-[var(--text-ter)]">◆</span>,
                    initiative: () => <span className="text-[var(--text-ter)]">◆</span>,
                    epic: () => <span className="text-[var(--text-ter)]">◆</span>,
                    feature: () => <span className="text-[var(--green)] dark:text-[var(--text-ter)]">◆</span>,
                    story: () => <span className="text-[var(--mustard)]">◆</span>,
                    task: () => <span className="text-[var(--text-sec)]">◆</span>,
                  }[alt];
                  
                  return (
                    <div key={alt} className="flex items-center gap-2">
                      <Icon />
                      <span className="text-[var(--text-sec)]">{altitudeText.format(alt, null, { short: true })}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
